import React, { useEffect, useRef, useState } from 'react';
import { chatStyles, ImageViewProps, MediaAction } from '@zonofi/chats-common';
import { theme } from '@zonofi/common';
import { ActivityIndicator } from 'react-native';
import { SHARED_SVGS } from '@zonofi/shared';
import { loadImageBlob, getCachedImageBlob } from './imageBlobCache';
import { useMediaHandler } from '../../helpers/handle-media';

const MAX_RETRIES = 3;

function isPresignedS3Url(url: string): boolean {
  const u = new URL(url);
  return (
    u.searchParams.has('X-Amz-Algorithm') &&
    u.searchParams.has('X-Amz-Credential') &&
    u.searchParams.has('X-Amz-Signature')
  );
}

export const ImageView: React.FC<ImageViewProps> = (props) => {
  const {
    messageId,
    filename = '',
    templateName,
    caption,
    generateLinkParams,
  } = props;

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [hasFailed, setHasFailed] = useState(false);
  const retryRef = useRef(0);
  const forceRef = useRef(false);

  const { handleMedia, isLoading } = useMediaHandler({
    ...props,
    maxRetries: 0,
    onRender: async (url) => {
      try {
        const isPrivate = isPresignedS3Url(url);

        if (!isPrivate) {
          setImageSrc(url);
          return;
        }

        const blobUrl = await loadImageBlob(messageId, url);
        if (!blobUrl) {
          if (retryRef.current < MAX_RETRIES) {
            retryRef.current += 1;
            await callHandleMedia('render', true);
          } else {
            console.error('Image failed to load after max retries.');
            setHasFailed(true);
          }
          return;
        }

        retryRef.current = 0;
        setHasFailed(false);
        setImageSrc(blobUrl);
      } catch (err) {
        if (retryRef.current < MAX_RETRIES) {
          retryRef.current += 1;
          await callHandleMedia('render', true);
        } else {
          console.error('Image failed to load after max retries:', err);
          setHasFailed(true);
        }
      }
    },
  });

  const callHandleMedia = async (action: MediaAction, force = false) => {
    if (action === 'render' && force) {
      forceRef.current = true;
    }

    await handleMedia(action, force || forceRef.current);
  };

  // ✅ Load cached image or trigger render
  useEffect(() => {
    const cached = getCachedImageBlob(messageId);
    if (cached) {
      setImageSrc(cached);
      setHasFailed(false);
      return;
    }

    callHandleMedia('render');
  }, [messageId]);

  const openImage = async () => {
    await callHandleMedia('open');
  };

  const downloadImage = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    await callHandleMedia('download');
  };

  const handleImageError = async () => {
    console.warn(
      'Image failed to load — retrying via media handler (forced S3)'
    );
    if (retryRef.current < MAX_RETRIES) {
      retryRef.current += 1;
      await handleMedia('render', true);
    } else {
      setHasFailed(true);
    }
  };

  const isCurrentlyLoading = (!imageSrc && !hasFailed) || isLoading;

  return (
    <div className="image-wrapper">
      {isCurrentlyLoading ? (
        <ActivityIndicator size={20} color={theme.text.secondary} />
      ) : hasFailed ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
            padding: '16px 12px',
            ...chatStyles.fontRegular12Failure,
          }}
        >
          <span style={{ fontSize: 20 }}>⚠️</span>
          Failed to load image
        </div>
      ) : (
        <img
          className="template-image"
          src={imageSrc!}
          alt={filename || templateName}
          onError={handleImageError}
          onClick={openImage}
        />
      )}

      {caption && <div className="image-caption">{caption}</div>}

      {generateLinkParams && (
        <button
          className="image-view__download-button"
          onClick={downloadImage}
          disabled={isLoading}
        >
          {isLoading ? (
            <ActivityIndicator size={20} color={theme.text.secondary} />
          ) : (
            <SHARED_SVGS.DownloadComponent className="custom-download-icon" />
          )}
        </button>
      )}
    </div>
  );
};
